import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import AuthLayout from "@/pages/auth/components/AuthLayout";
import { supabase } from "@/lib/supabase";

function getPasswordStrength(password: string): {
  score: number;
  label: string;
  color: string;
  barColor: string;
  tips: string[];
} {
  let score = 0;
  const tips: string[] = [];

  if (password.length >= 8) score++;
  else tips.push("At least 8 characters");

  if (password.length >= 12) score++;

  if (/[A-Z]/.test(password)) score++;
  else tips.push("One uppercase letter");

  if (/[0-9]/.test(password)) score++;
  else tips.push("One number");

  if (/[^A-Za-z0-9]/.test(password)) score++;
  else tips.push("One special character (!@#$...)");

  if (score <= 1) return { score, label: "Weak", color: "text-red-500", barColor: "bg-red-400", tips };
  if (score === 2) return { score, label: "Fair", color: "text-amber-500", barColor: "bg-amber-400", tips };
  if (score === 3) return { score, label: "Good", color: "text-yellow-600", barColor: "bg-yellow-400", tips };
  if (score === 4) return { score, label: "Strong", color: "text-[#16a34a]", barColor: "bg-[#16a34a]", tips };
  return { score, label: "Very Strong", color: "text-[#16a34a]", barColor: "bg-[#16a34a]", tips };
}

const US_STATES = [
  "New Jersey",
  "New York",
  "Pennsylvania",
  "Connecticut",
  "Delaware",
  "Maryland",
  "Massachusetts",
  "Rhode Island",
];

export default function SignupPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = account info, 2 = service area
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [zipInput, setZipInput] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [form, setForm] = useState({
    company: "",
    contact: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    service_states: [] as string[],
    service_zips: [] as string[],
  });

  const passwordStrength = useMemo(() => getPasswordStrength(form.password), [form.password]);

  const update = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: "" }));
  };

  const toggleState = (state: string) => {
    setForm(prev => ({
      ...prev,
      service_states: prev.service_states.includes(state)
        ? prev.service_states.filter(s => s !== state)
        : [...prev.service_states, state],
    }));
  };

  const addZip = () => {
    const zip = zipInput.trim();
    if (!/^\d{5}$/.test(zip)) {
      setErrors(prev => ({ ...prev, zip: "Enter a valid 5-digit ZIP code" }));
      return;
    }
    if (form.service_zips.includes(zip)) {
      setErrors(prev => ({ ...prev, zip: "ZIP already added" }));
      return;
    }
    setForm(prev => ({ ...prev, service_zips: [...prev.service_zips, zip] }));
    setZipInput("");
    setErrors(prev => ({ ...prev, zip: "" }));
  };

  const removeZip = (zip: string) => {
    setForm(prev => ({ ...prev, service_zips: prev.service_zips.filter(z => z !== zip) }));
  };

  const validateStep1 = () => {
    const newErrors: Record<string, string> = {};
    if (!form.company.trim()) newErrors.company = "Company name is required";
    if (!form.contact.trim()) newErrors.contact = "Contact name is required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = "Valid email is required";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 10) newErrors.phone = "Valid phone number is required";
    if (form.password.length < 8) newErrors.password = "Password must be at least 8 characters";
    if (form.password !== form.confirmPassword) newErrors.confirmPassword = "Passwords do not match";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors: Record<string, string> = {};
    if (form.service_states.length === 0) newErrors.service_states = "Select at least one state";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep1()) setStep(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep2()) return;
    setLoading(true);

    try {
      // 1. Create Supabase Auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
        options: {
          data: {
            company: form.company,
            contact: form.contact,
          },
        },
      });

      if (authError) {
        // Surface friendly messages for common errors
        if (authError.message.toLowerCase().includes("already registered")) {
          setErrors({ submit: "This email is already registered. Please sign in instead." });
        } else {
          setErrors({ submit: authError.message });
        }
        setLoading(false);
        return;
      }

      const userId = authData.user?.id;
      if (!userId) {
        setErrors({ submit: "Signup failed — could not create account. Please go back and try a different email." });
        setLoading(false);
        return;
      }

      // Supabase silently returns a fake user for already-registered emails when confirmation is on
      // Detect this: identities array is empty for existing users
      if (authData.user?.identities && authData.user.identities.length === 0) {
        setErrors({ submit: "This email is already registered. Please sign in instead." });
        setLoading(false);
        return;
      }

      // 2. Insert installer profile via edge function (bypasses RLS for unconfirmed sessions)
      let res;
      try {
        res = await fetch(
          "https://ukfoujowiwsrrmajzajy.supabase.co/functions/v1/register-installer",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: userId,
              company: form.company,
              contact: form.contact,
              email: form.email,
              phone: form.phone,
              service_states: form.service_states,
              service_zips: form.service_zips,
            }),
          }
        );
      } catch (fetchErr) {
        setErrors({ submit: "Network error — could not connect to server. Please check your connection and try again." });
        setLoading(false);
        return;
      }

      let result;
      try {
        result = await res.json();
      } catch {
        setErrors({ submit: "Server error — unexpected response. Please try again." });
        setLoading(false);
        return;
      }

      if (!res.ok || result.error) {
        setErrors({ submit: result.error || `Failed to save your profile (HTTP ${res.status}). Please try again.` });
        setLoading(false);
        return;
      }

      // 3. Store signup data for contract page
      sessionStorage.setItem("signup_data", JSON.stringify({
        company: form.company,
        contact: form.contact,
        email: form.email,
        phone: form.phone,
        service_states: form.service_states,
        service_zips: form.service_zips,
        user_id: userId,
      }));

      setLoading(false);
      navigate("/contract");
    } catch (err) {
      setErrors({ submit: "An unexpected error occurred. Please try again." });
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={step === 1 ? "Create Your Installer Account" : "Set Your Service Area"}
      subtitle={step === 1 ? "Join the network of top solar installers getting exclusive leads." : "Tell us where you install solar. You can update this anytime."}
      step={1}
      totalSteps={4}
      subStep={step}
      totalSubSteps={2}
    >
      {step === 1 ? (
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Company Name *</label>
              <input
                type="text"
                value={form.company}
                onChange={e => update("company", e.target.value)}
                placeholder="SunPower Solutions LLC"
                className={`w-full px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition ${errors.company ? "border-red-400 bg-red-50" : "border-gray-200 bg-gray-50"}`}
              />
              {errors.company && <p className="text-red-500 text-xs mt-1">{errors.company}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Contact Name *</label>
              <input
                type="text"
                value={form.contact}
                onChange={e => update("contact", e.target.value)}
                placeholder="John Smith"
                className={`w-full px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition ${errors.contact ? "border-red-400 bg-red-50" : "border-gray-200 bg-gray-50"}`}
              />
              {errors.contact && <p className="text-red-500 text-xs mt-1">{errors.contact}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Business Email *</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={e => update("email", e.target.value)}
              placeholder="john@sunpowersolutions.com"
              className={`w-full px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition ${errors.email ? "border-red-400 bg-red-50" : "border-gray-200 bg-gray-50"}`}
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number *</label>
            <input
              type="tel"
              value={form.phone}
              onChange={e => update("phone", e.target.value)}
              placeholder="(555) 000-0000"
              className={`w-full px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition ${errors.phone ? "border-red-400 bg-red-50" : "border-gray-200 bg-gray-50"}`}
            />
            {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Password *</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={e => update("password", e.target.value)}
                placeholder="Min. 8 characters"
                className={`w-full px-4 py-2.5 pr-11 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition ${errors.password ? "border-red-400 bg-red-50" : "border-gray-200 bg-gray-50"}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
              </button>
            </div>

            {/* Strength meter */}
            {form.password.length > 0 && (
              <div className="mt-2 space-y-1.5">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                        i <= passwordStrength.score ? passwordStrength.barColor : "bg-gray-200"
                      }`}
                    />
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-semibold ${passwordStrength.color}`}>
                    {passwordStrength.label}
                  </span>
                  {passwordStrength.tips.length > 0 && (
                    <span className="text-xs text-gray-400 truncate ml-2">
                      Add: {passwordStrength.tips[0].toLowerCase()}
                    </span>
                  )}
                </div>
              </div>
            )}
            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password *</label>
            <div className="relative">
              <input
                type={showConfirm ? "text" : "password"}
                value={form.confirmPassword}
                onChange={e => update("confirmPassword", e.target.value)}
                placeholder="Repeat password"
                className={`w-full px-4 py-2.5 pr-11 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition ${
                  errors.confirmPassword
                    ? "border-red-400 bg-red-50"
                    : form.confirmPassword.length > 0 && form.confirmPassword === form.password
                    ? "border-green-400 bg-green-50/30"
                    : "border-gray-200 bg-gray-50"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowConfirm(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className={showConfirm ? "ri-eye-off-line" : "ri-eye-line"}></i>
              </button>
            </div>
            {form.confirmPassword.length > 0 && form.confirmPassword === form.password && !errors.confirmPassword && (
              <p className="text-[#16a34a] text-xs mt-1 flex items-center gap-1">
                <i className="ri-checkbox-circle-line"></i> Passwords match
              </p>
            )}
            {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
          </div>

          <button
            type="button"
            onClick={handleNext}
            className="w-full bg-[#16a34a] hover:bg-[#15803d] text-white font-semibold py-3 rounded-lg text-sm transition-all whitespace-nowrap cursor-pointer"
          >
            Continue to Service Area <i className="ri-arrow-right-line ml-1"></i>
          </button>

          <p className="text-center text-sm text-gray-500">
            Already have an account?{" "}
            <Link to="/login" className="text-[#16a34a] font-medium hover:underline">Sign in</Link>
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
          {/* General error banner */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg flex items-start gap-2">
              <i className="ri-error-warning-line flex-shrink-0 mt-0.5"></i>
              <span>{errors.submit}</span>
            </div>
          )}

          {/* States */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Service States * <span className="text-gray-400 font-normal">(select all that apply)</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-1">
              {US_STATES.map(state => (
                <button
                  key={state}
                  type="button"
                  onClick={() => toggleState(state)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all cursor-pointer text-left whitespace-nowrap ${
                    form.service_states.includes(state)
                      ? "bg-[#16a34a] text-white border-[#16a34a]"
                      : "bg-gray-50 text-gray-600 border-gray-200 hover:border-green-400"
                  }`}
                >
                  {form.service_states.includes(state) && <i className="ri-check-line mr-1"></i>}
                  {state}
                </button>
              ))}
            </div>
            {errors.service_states && <p className="text-red-500 text-xs mt-2">{errors.service_states}</p>}
            {form.service_states.length > 0 && (
              <p className="text-green-600 text-xs mt-2 font-medium">
                <i className="ri-check-line mr-1"></i>{form.service_states.length} state{form.service_states.length > 1 ? "s" : ""} selected
              </p>
            )}
          </div>

          {/* ZIP codes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Target ZIP Codes <span className="text-gray-400 font-normal">(optional — for precise targeting)</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={zipInput}
                onChange={e => setZipInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addZip())}
                placeholder="e.g. 90210"
                maxLength={5}
                className={`flex-1 px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition ${errors.zip ? "border-red-400 bg-red-50" : "border-gray-200 bg-gray-50"}`}
              />
              <button
                type="button"
                onClick={addZip}
                className="px-4 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition whitespace-nowrap cursor-pointer"
              >
                Add ZIP
              </button>
            </div>
            {errors.zip && <p className="text-red-500 text-xs mt-1">{errors.zip}</p>}
            {form.service_zips.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {form.service_zips.map(zip => (
                  <span key={zip} className="inline-flex items-center gap-1 bg-green-50 text-green-700 border border-green-200 px-3 py-1 rounded-full text-xs font-medium">
                    {zip}
                    <button type="button" onClick={() => removeZip(zip)} className="hover:text-red-500 transition cursor-pointer">
                      <i className="ri-close-line"></i>
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="flex-1 border border-gray-200 text-gray-600 font-semibold py-3 rounded-lg text-sm hover:bg-gray-50 transition whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-1"></i> Back
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-semibold py-3 rounded-lg text-sm transition-all whitespace-nowrap cursor-pointer"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i> Creating account...
                </span>
              ) : (
                <>Create Account &amp; Continue <i className="ri-arrow-right-line ml-1"></i></>
              )}
            </button>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
